// Re-export shared types and helpers
export type { WordTiming, KenBurnsConfig, Scene, LogoSceneConfig } from '../shared/types';
export { msToFrames, getCurrentScene, getVisibleWords, KEN_BURNS_PRESETS } from '../shared/types';
import type { WordTiming, Scene } from '../shared/types';
import { KEN_BURNS_PRESETS } from '../shared/types';

// Color palettes for each scene - Search/Ranking theme
export const SCENE_COLORS = {
  scene1: { primaryColor: '#3B82F6', accentColor: '#ffffff' },   // Blue/White - Searching
  scene2: { primaryColor: '#8B5CF6', accentColor: '#ffffff' },   // Purple/White - Platforms
  scene3: { primaryColor: '#F59E0B', accentColor: '#ffffff' },   // Amber/White - Question
  scene4: { primaryColor: '#ffffff', accentColor: '#EF4444' },   // White/Red - Guessing
  scene5: { primaryColor: '#22C55E', accentColor: '#ffffff' },   // Green/White - Truth
  scene6: { primaryColor: '#FCD34D', accentColor: '#ffffff' },   // Gold/White - CTA
  logo: { primaryColor: '#ffffff', accentColor: '#FCD34D' },     // White/Gold - Brand
};

// Background images for each scene
export const SCENE_IMAGES = {
  scene1: 'images/gdm-where-do-you-rank/scene1-searching.png',
  scene2: 'images/gdm-where-do-you-rank/scene2-platforms.png',
  scene3: 'images/gdm-where-do-you-rank/scene3-question.png',
  scene4: 'images/gdm-where-do-you-rank/scene4-guessing.png',
  scene5: 'images/gdm-where-do-you-rank/scene5-truth.png',
  scene6: 'images/gdm-where-do-you-rank/scene6-report.png',
};

// Report slides (reuse from GDM quiet phone)
export const REPORT_SLIDES = [
  'images/gdm-quiet-phone/report-slide-01.png',
  'images/gdm-quiet-phone/report-slide-05.png',
  'images/gdm-quiet-phone/report-slide-07.png',
  'images/gdm-quiet-phone/report-slide-12.png',
];

// Dramatic words
export const DRAMATIC_WORDS = ['rank?', 'don\'t.', 'truth.', 'Free', 'Report.'];

// Word timings from Whisper — manual corrections: "10" display as "Ten"
export const WORD_TIMINGS: WordTiming[] = [
  // Scene 1: Hook — searching
  { text: 'Your', startMs: 0, endMs: 80 },
  { text: 'customers', startMs: 80, endMs: 480 },
  { text: 'are', startMs: 480, endMs: 660 },
  { text: 'searching', startMs: 660, endMs: 880 },
  { text: 'right', startMs: 880, endMs: 1080 },
  { text: 'now.', startMs: 1080, endMs: 1420 },
  { text: 'Best', startMs: 1420, endMs: 2100 },
  { text: 'garage', startMs: 2100, endMs: 2400 },
  { text: 'door', startMs: 2400, endMs: 2640 },
  { text: 'company', startMs: 2640, endMs: 2960 },
  { text: 'near', startMs: 2960, endMs: 3200 },
  { text: 'me.', startMs: 3200, endMs: 4120 },

  // Scene 2: Platforms
  { text: 'Google.', startMs: 4120, endMs: 5160 },
  { text: 'Yelp.', startMs: 5160, endMs: 6160 },
  { text: 'ChatGPT.', startMs: 6160, endMs: 7440 },

  // Scene 3: Question
  { text: 'Do', startMs: 7440, endMs: 8460 },
  { text: 'you', startMs: 8460, endMs: 8540 },
  { text: 'know', startMs: 8540, endMs: 8680 },
  { text: 'where', startMs: 8680, endMs: 8800 },
  { text: 'you', startMs: 8800, endMs: 8920 },
  { text: 'rank?', startMs: 8920, endMs: 9480 },

  // Scene 4: Guessing
  { text: 'Most', startMs: 9480, endMs: 10640 },
  { text: 'garage', startMs: 10640, endMs: 10940 },
  { text: 'door', startMs: 10940, endMs: 11160 },
  { text: 'companies', startMs: 11160, endMs: 11520 },
  { text: "don't.", startMs: 11520, endMs: 11980 },
  { text: 'They', startMs: 11980, endMs: 12680 },
  { text: 'guess.', startMs: 12680, endMs: 13120 },
  { text: 'They', startMs: 13120, endMs: 13500 },
  { text: 'hope.', startMs: 13500, endMs: 14240 },

  // Scene 5: Truth / Solution
  { text: "We'll", startMs: 14240, endMs: 15460 },
  { text: 'show', startMs: 15460, endMs: 15600 },
  { text: 'you', startMs: 15600, endMs: 15720 },
  { text: 'the', startMs: 15720, endMs: 15820 },
  { text: 'truth.', startMs: 15820, endMs: 16220 },
  { text: 'Your', startMs: 16220, endMs: 16820 },
  { text: 'exact', startMs: 16820, endMs: 17060 },
  { text: 'position.', startMs: 17060, endMs: 17560 },
  { text: 'Your', startMs: 17560, endMs: 17980 },
  { text: 'competitors.', startMs: 17980, endMs: 18660 },
  { text: 'The', startMs: 18660, endMs: 19260 },
  { text: 'gaps', startMs: 19260, endMs: 19500 },
  { text: 'costing', startMs: 19500, endMs: 19840 },
  { text: 'you', startMs: 19840, endMs: 20040 },
  { text: 'jobs.', startMs: 20040, endMs: 20500 },

  // Scene 6: CTA
  { text: 'Free', startMs: 20500, endMs: 21080 },
  { text: 'Market', startMs: 21080, endMs: 21360 },
  { text: 'Report.', startMs: 21360, endMs: 22100 },
  { text: 'Ten', startMs: 22100, endMs: 22720 },
  { text: 'minutes.', startMs: 22720, endMs: 23140 },
  { text: 'Link', startMs: 23140, endMs: 23480 },
  { text: 'below.', startMs: 23480, endMs: 23900 },
];

// Scene definitions
export const SCENES: Scene[] = [
  {
    id: 1,
    name: 'Hook',
    startMs: 0,
    endMs: 4120,
    words: WORD_TIMINGS.slice(0, 12),
    ...SCENE_COLORS.scene1,
    backgroundImage: SCENE_IMAGES.scene1,
    kenBurns: KEN_BURNS_PRESETS.zoomIn,
  },
  {
    id: 2,
    name: 'Platforms',
    startMs: 4120,
    endMs: 7440,
    words: WORD_TIMINGS.slice(12, 15),
    ...SCENE_COLORS.scene2,
    backgroundImage: SCENE_IMAGES.scene2,
    kenBurns: KEN_BURNS_PRESETS.panRight,
  },
  {
    id: 3,
    name: 'Question',
    startMs: 7440,
    endMs: 9480,
    words: WORD_TIMINGS.slice(15, 21),
    ...SCENE_COLORS.scene3,
    backgroundImage: SCENE_IMAGES.scene3,
    kenBurns: KEN_BURNS_PRESETS.dramaticZoom,
  },
  {
    id: 4,
    name: 'Guessing',
    startMs: 9480,
    endMs: 14240,
    words: WORD_TIMINGS.slice(21, 30),
    ...SCENE_COLORS.scene4,
    backgroundImage: SCENE_IMAGES.scene4,
    kenBurns: KEN_BURNS_PRESETS.zoomOut,
  },
  {
    id: 5,
    name: 'Truth',
    startMs: 14240,
    endMs: 20500,
    words: WORD_TIMINGS.slice(30, 45),
    ...SCENE_COLORS.scene5,
    backgroundImage: SCENE_IMAGES.scene5,
    kenBurns: KEN_BURNS_PRESETS.panLeft,
  },
  {
    id: 6,
    name: 'CTA',
    startMs: 20500,
    endMs: 23900,
    words: WORD_TIMINGS.slice(45),
    ...SCENE_COLORS.scene6,
    backgroundImage: SCENE_IMAGES.scene6,
    kenBurns: KEN_BURNS_PRESETS.zoomWithTilt,
  },
];

// Logo scene
export const LOGO_SCENE = {
  startMs: 23900,
  endMs: 29000,
  ...SCENE_COLORS.logo,
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
